import { skipToken } from "@reduxjs/toolkit/query";

import { useModelRevisions } from "apis/procore/modelRevisions";
import { useGetModelsQuery } from "apis/procore/procoreApi";
import { useAppSelector } from "app/redux-store-interactions";
import {
    isDevForceMobile,
    selectHostSceneTitle,
    selectProcoreContext,
    selectProjectName,
} from "slices/explorer/selectors";

/**
 * @returns The host-provided scene title (`InitOptions.sceneTitle`) when set, otherwise the Procore
 * model name of the open scene, `""` when there is none. The models request is skipped when the host
 * supplies a title. In `npm run dev` with `?forceMobile` (no Procore backend) it falls back to the
 * project name, then a placeholder, so the mobile header styling can be inspected.
 */
export function useSceneTitle(): { title: string; isLoading: boolean } {
    const procoreContext = useAppSelector(selectProcoreContext);
    const projectName = useAppSelector(selectProjectName);
    const hostSceneTitle = useAppSelector(selectHostSceneTitle);

    const { data: modelRevisionsData, isLoading: isModelRevisionsLoading } = useModelRevisions();
    const modelRevision = modelRevisionsData?.[0];

    const { data: modelsData, isLoading: isModelsLoading } = useGetModelsQuery(
        procoreContext && !hostSceneTitle
            ? {
                  companyId: procoreContext.companyId,
                  projectId: procoreContext.projectId,
              }
            : skipToken,
    );
    const model = modelRevision ? modelsData?.data.find((m) => m.id === modelRevision.bim_model_id) : undefined;

    return {
        title: hostSceneTitle || model?.name || (isDevForceMobile ? projectName || "Sample model name" : ""),
        isLoading: !hostSceneTitle && (isModelRevisionsLoading || isModelsLoading),
    };
}
